/**
 * FAQ Article Content
 */

export const faqContent = `---
id: faq-questions
title: Frequently Asked Questions
description: Account, organizations, bowling centers, Setup, scoring, averages, brackets, and payouts.
category: faqs
type: faq
estimatedTime: 10 min read
path: /support/faqs/account-questions
lastUpdated: 2026-08-20
version: 3.0
tags:
  - faq
  - questions
  - support
  - help
seo:
  title: Frequently Asked Questions - Bowling Tournament Support
  description: Answers about accounts, organizations, Setup, scoring, averages, brackets, and payouts.
  keywords:
    - bowling tournament FAQ
    - tournament management questions
    - account support
    - payment help
    - bowling brackets
---

# Frequently Asked Questions

## Account

### How do I create an account?

Click **Create Tournament** or **Sign up**. New accounts create an organization in that flow. Your email is your login and cannot be changed later.

### I forgot my password.

Click **Forgot password** on the login page. Check spam if you do not see the email.

### How do I delete my account?

Contact [support](/support/contact). You cannot delete an account from profile settings. Deletion is permanent.

### Is my personal information secure?

See the [privacy policy](/privacy-policy).

## Where things are

### Where do I add events and squads?

**Setup**.

Name, date, handicap, and payout ratio are on **Settings**. Payouts, added money, and payout table are on **Finance**. **Overview** is the public tournament page.

### Do I have to turn on online signups?

No. Add teams on **Scoring**. Signups are optional.

### Can I edit a tournament after it is created?

Yes, until you verify scores or lock payouts. After that, unverify or unlock first. Hover over a disabled button to see why.

### How do I delete a tournament?

**Settings**. Remove teams, then squads, then delete. Pay now Cancel until 24 hours before the squad is this app’s refund window, not Stripe’s. See the [refund policy](/refund-policy). Other refunds are handled by the organization.

## Brackets

### How do I sell bowling brackets?

Add a bracket on **Setup**. Sell tickets on **Create Team**, **Edit Team**, or **Brackets**. Close sales and shuffle on **Brackets** when that squad is ready. See [How to run bowling brackets](/support/tutorials/run-brackets).

### What kind of brackets can I run?

Traditional 8-player trees. Scratch and handicap are separate. A tree of 7 can have a first-round bye. Six or fewer leftover tickets are refunded.

## Handicap and payouts

### How is handicap calculated?

(Base − average) × percentage, rounded down, then limited if you set a maximum. Example: base 220, 90%, maximum 75. A 180 average gets 36.

Change handicap on **Settings**. Change the average on **Averages**.

### What is the payout ratio?

1 in 6 means one place paid for every six entries. The app uses this to suggest how many places to pay. Dollar amounts come from the payout table times the event prize fund. You can change places and amounts on **Finance → Payouts** before you lock.

### How are payouts calculated?

Each event's prize fund (prize-fund fees plus added money) is split by the payout table. Totals on **Payouts** must match the prize fund before you lock. You can lock payouts only after scores are verified.

This app does not send prize money. If Card payments is on, bowlers can pay with Pay now. Add to cart holds the spot for 24 hours. Pay now or Pay at the desk closes Cart. That includes a 2% platform fee plus card processing. A Pay now refund returns the advertised entry and the 2% platform fee. Card processing is not refunded. See the [refund policy](/refund-policy).

## Scoring and averages

### Can I change bowlers on a team?

No. Delete the team and create a new one.

### Why can't I verify scores?

This team's averages must be verified first. Another team's average does not block this team. Open **Averages**. **Verify** is one row. **Verify all** is every remaining row. Both count the same. Last name on that table is taken from the full name.

### Why can't I lock payouts?

Scores must be verified, added money must be assigned to events, and each event payout total must match its prize fund.

## Organizations

### What is an organization?

Who runs the tournaments. A bowling center is the house. Those can be the same business, and they can use the same name. One organization can also use more than one bowling center.

### How do I invite members?

Organization **Members**, then **Invite Member**. Owners and organization admins can change roles there. If you are the owner and want to leave, transfer ownership first.

Roles: Owner (can delete the organization), Admin (cannot delete the organization), Tournament Official (tournament work).

## Technical

### What browsers are supported?

Current Chrome, Firefox, Safari, and Edge. There is no mobile app. The site works in a phone browser.

### How do I contact support?

[Submit a ticket](/support/contact). Include the steps you took, your browser, and the exact error text.

Two-factor authentication is not supported. If you think your account was used by someone else, change your password, then contact support.

---

[Contact support](/support/contact)

- [Getting started](/support/tutorials/getting-started)
- [How to create a bowling tournament](/support/tutorials/create-tournament)
- [How to run a bowling tournament](/support/tutorials/run-a-tournament)
- [How to run bowling brackets](/support/tutorials/run-brackets)
`;

export default faqContent;
